import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch {
      setNotifs({ items: [], unread: 0 });
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);
  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };
  const openNotification = async (n) => {
    try {
      if (!n.read_at) await api.post(`/notifications/${n.id}/read`);
    } catch {
      // Non-blocking: still navigate if the notification has a target.
    }
    if (n.href) navigate(n.href);
    load();
  };
  const tone = (n) => {
    if (n.type === "trust_health") return "border-l-2 border-l-amber-500";
    if (n.type === "watchlist_digest") return "border-l-2 border-l-sky-500";
    return "";
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-8 h-8 grid place-items-center text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 rounded-md transition-colors"
          aria-label="Notifications"
        >
          <Bell size={16} strokeWidth={1.8} />
          {notifs.unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-semibold grid place-items-center px-1"
            >
              {notifs.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-zinc-950 border-zinc-800">
        <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
          <span className="text-xs font-semibold text-zinc-200 uppercase tracking-wider">Notifications</span>
          <button
            onClick={markAll}
            data-testid="btn-mark-all-read"
            className="text-[10px] text-zinc-400 hover:text-zinc-100"
          >
            Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-auto">
          {notifs.items.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-500">No notifications yet.</div>
          ) : (
            notifs.items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openNotification(n)}
                className={`block w-full text-left px-4 py-2.5 border-b border-zinc-800/50 text-xs hover:bg-zinc-900/60 transition-colors ${tone(n)} ${
                  n.read_at ? "text-zinc-500" : "text-zinc-200"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-snug">{n.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{n.body}</div>
                  </div>
                  {n.href && <ExternalLink size={12} className="mt-0.5 shrink-0 text-zinc-500" />}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
